import React, { useEffect, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FiArrowLeft } from 'react-icons/fi';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import axios from 'axios';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureUser, userDisplayNameAsString } from '../../../util/data';
import { apiBaseUrl } from '../../../util/api';
import {
  isErrorNoPermissionForInitiateTransactions,
  isErrorNoPermissionForUserPendingApproval,
  isTooManyRequestsError,
} from '../../../util/errors';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  AvatarLarge,
  ResponsiveImage,
  NamedLink,
} from '../../../components';

import css from './InquiryForm.module.css';
import { NO_ACCESS_PAGE_INITIATE_TRANSACTIONS } from '../../../util/urlHelpers';
import { ReactComponent as VerifySeller } from '../../../assets/svg/sellerVerifiedBadge.svg';

const ErrorMessage = props => {
  const { error } = props;
  const userPendingApproval = isErrorNoPermissionForUserPendingApproval(error);
  const userHasNoTransactionRights = isErrorNoPermissionForInitiateTransactions(error);
  const isRateLimit = error?.isRateLimitError || error?.message?.includes('New accounts are limited');

  return error ? (
    <p className={css.error}>
      {isRateLimit ? (
        <FormattedMessage
          id="InquiryForm.rateLimitExceeded"
          values={{
            maxHourly: error.maxHourly || 5,
            maxDaily: error.maxDaily || 10,
          }}
        />
      ) : error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="InquiryForm.sendInquiryErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="InquiryForm.tooManyRequestsError" />
      ) : userPendingApproval ? (
        <FormattedMessage id="InquiryForm.userPendingApprovalError" />
      ) : userHasNoTransactionRights ? (
        <FormattedMessage
          id="InquiryForm.noTransactionRightsError"
          values={{
            NoAccessLink: msg => (
              <NamedLink
                name="NoAccessPage"
                params={{ missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS }}
              >
                {msg}
              </NamedLink>
            ),
          }}
        />
      ) : (
        <FormattedMessage id="InquiryForm.sendInquiryError" />
      )}
    </p>
  ) : null;
};

/**
 * The InquiryForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class
 * @param {string} [props.rootClassName] - Root class
 * @param {boolean} [props.inProgress] - Whether in progress
 * @param {string} props.listingTitle - Listing title
 * @param {Object} props.listing - Listing entity
 * @param {string|Object} props.authorDisplayName - Author display name
 * @param {propTypes.error} props.sendInquiryError - Error object
 * @param {Function} props.onCloseInquiryModal - Callback to close modal
 * @returns {JSX.Element} InquiryForm component
 */
const InquiryForm = props => {
  const { listing, onCloseInquiryModal, currentUser, verifiedMail } = props;

  const author = ensureUser(listing?.author);
  const authorId = author?.id?.uuid;
  const isCurrentUser =
    authorId && currentUser?.id?.uuid && authorId === currentUser.id.uuid;

  const [isEmailVerified, setIsEmailVerified] = useState(
    isCurrentUser
      ? !!(currentUser?.attributes?.emailVerified ?? author?.attributes?.emailVerified)
      : typeof verifiedMail === 'boolean'
      ? verifiedMail
      : !!author?.attributes?.emailVerified
  );

  const [sellerRating, setSellerRating] = useState(
    author?.attributes?.profile?.publicData?.rating ||
    author?.attributes?.profile?.metadata?.rating ||
    author?.attributes?.profile?.publicData?.userRating ||
    null
  );
  const [sellerRatingCount, setSellerRatingCount] = useState(
    author?.attributes?.profile?.publicData?.ratingCount ||
    author?.attributes?.profile?.metadata?.ratingCount ||
    author?.attributes?.profile?.publicData?.userRatingCount ||
    null
  );
  const [sellerLastSeen, setSellerLastSeen] = useState(
    author?.attributes?.profile?.metadata?.lastSeen || null
  );

  useEffect(() => {
    if (isCurrentUser) {
      setIsEmailVerified(
        !!(currentUser?.attributes?.emailVerified ?? author?.attributes?.emailVerified)
      );
    } else if (typeof verifiedMail === 'boolean') {
      setIsEmailVerified(verifiedMail);
    } else if (author?.attributes?.emailVerified !== undefined) {
      setIsEmailVerified(!!author.attributes.emailVerified);
    }

    if (authorId) {
      axios
        .get(`${apiBaseUrl()}/api/email-verification/${authorId}`)
        .then(res => {
          if (res?.data) {
            if (res.data.emailVerified !== undefined && !isCurrentUser && typeof verifiedMail !== 'boolean') {
              setIsEmailVerified(!!res.data.emailVerified);
            }
            if (res.data.rating != null) {
              setSellerRating(res.data.rating);
            }
            if (res.data.ratingCount != null) {
              setSellerRatingCount(res.data.ratingCount);
            }
            if (res.data.lastSeen != null) {
              setSellerLastSeen(res.data.lastSeen);
            }
          }
        })
        .catch(() => {
          if (!isCurrentUser && typeof verifiedMail !== 'boolean' && author?.attributes?.emailVerified === undefined) {
            setIsEmailVerified(false);
          }
        });
    }
  }, [authorId, isCurrentUser, currentUser?.attributes?.emailVerified, verifiedMail]);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          form,
          handleSubmit,
          inProgress = false,
          listingTitle,
          authorDisplayName,
          sendInquiryError,
          invalid,
          values = {},
        } = fieldRenderProps;

        const intl = useIntl();
        const storeDisplayName =
          author?.attributes?.profile?.publicData?.rawProfileUrl ||
          author?.attributes?.profile?.publicData?.profileUrl;
        const authorName =
          typeof authorDisplayName === 'string'
            ? authorDisplayName
            : storeDisplayName || userDisplayNameAsString(author, '');
        const displayNameDisplay =
          storeDisplayName ||
          authorName ||
          author?.attributes?.profile?.displayName ||
          'SELLER';

        const rating =
          sellerRating ||
          author?.attributes?.profile?.publicData?.rating ||
          author?.attributes?.profile?.metadata?.rating ||
          author?.attributes?.profile?.publicData?.userRating;
        const ratingCount =
          sellerRatingCount ||
          author?.attributes?.profile?.publicData?.ratingCount ||
          author?.attributes?.profile?.metadata?.ratingCount ||
          author?.attributes?.profile?.publicData?.userRatingCount;

        const renderRating = () => {
          const numRating = Number(rating);
          const numRatingCount = Number(ratingCount);
          if (isNaN(numRating) || numRating <= 0 || isNaN(numRatingCount) || numRatingCount <= 0) {
            return null;
          }
          const displayRating =
            numRating % 1 === 0 ? numRating.toString() : numRating.toFixed(1);
          return (
            <div className={css.ratingRow}>
              <svg className={css.starIcon} width="14" height="14" viewBox="0 0 24 24" fill="#fec000" stroke="#fec000">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              <span className={css.ratingValue}>{displayRating}</span>
              <span className={css.ratingCount}>({numRatingCount})</span>
            </div>
          );
        };

        const lastActiveAt = sellerLastSeen || author?.attributes?.profile?.metadata?.lastSeen;

        const renderActivityStatus = () => {
          if (!lastActiveAt) {
            return (
              <div className={css.activityStatus}>
                <span className={classNames(css.dot, css.dotGreen)} />
                <span className={css.activityLabel}>
                  <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
                </span>
              </div>
            );
          }
          const timeDiffMs = new Date().getTime() - new Date(lastActiveAt).getTime();
          if (isNaN(timeDiffMs)) {
            return (
              <div className={css.activityStatus}>
                <span className={classNames(css.dot, css.dotGreen)} />
                <span className={css.activityLabel}>
                  <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
                </span>
              </div>
            );
          }
          const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
          const timeDiffDays = Math.floor(timeDiffHours / 24);

          if (timeDiffHours < 24) {
            return (
              <div className={css.activityStatus}>
                <span className={classNames(css.dot, css.dotGreen)} />
                <span className={css.activityLabel}>
                  <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
                </span>
              </div>
            );
          } else if (timeDiffDays < 15) {
            const days = Math.max(1, timeDiffDays);
            return (
              <div className={css.activityStatus}>
                <span className={classNames(css.dot, css.dotAmber)} />
                <span className={css.activityLabel}>
                  <FormattedMessage
                    id="UserCard.activeDaysAgo"
                    defaultMessage="Active {days} {days, plural, one {day} other {days}} ago"
                    values={{ days }}
                  />
                </span>
              </div>
            );
          } else if (timeDiffDays < 60) {
            const weeks = Math.floor(timeDiffDays / 7);
            return (
              <div className={css.activityStatus}>
                <span className={classNames(css.dot, css.dotGrey)} />
                <span className={css.activityLabel}>
                  <FormattedMessage
                    id="UserCard.activeWeeksAgo"
                    defaultMessage="Active {weeks} {weeks, plural, one {week} other {weeks}} ago"
                    values={{ weeks }}
                  />
                </span>
              </div>
            );
          }

          return null;
        };

        const messagePlaceholder = `Hey ${authorName || 'there'}! just wanted to ask a quick question about this item.`;
        const messageRequiredMessage = intl.formatMessage({
          id: 'InquiryForm.messageRequired',
        });
        const noExternalLinksMessage = intl.formatMessage({
          id: 'validators.noExternalLinks',
        });
        const messageValidators = validators.composeValidators(
          validators.requiredAndNonEmptyString(messageRequiredMessage),
          validators.noExternalLinks(noExternalLinksMessage)
        );

        const firstImage = listing?.images?.length > 0 ? listing.images[0] : null;
        const publicData = listing?.attributes?.publicData || {};
        const categoryLevel1 = publicData.categoryLevel1;
        const categoryLevel2 = publicData.categoryLevel2;

        const sizeVal =
          categoryLevel2 === 'Women-Accessories' || categoryLevel2 === 'men-accessories'
            ? publicData?.Women_Men_Accessories_size
            : categoryLevel2 === 'men-shoes'
            ? publicData?.Mens_Shoes_Sizes
            : categoryLevel2 === 'women-shoes'
            ? publicData?.Womens_Shoes_Sizes
            : publicData?.size ||
              publicData?.sizeName ||
              publicData?.itemSize ||
              publicData?.clothingSize ||
              publicData?.women_size ||
              publicData?.men_sizes;

        const rawCondition =
          publicData.condition || publicData.itemCondition || publicData.itemConditionName;
        const conditionVal = rawCondition
          ? rawCondition.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          : null;

        const sizeConditionText = [sizeVal ? `Size ${sizeVal}` : null, conditionVal]
          .filter(Boolean)
          .join(' • ');
        const priceVal = listing?.attributes?.price;

        const handlePromptClick = promptText => {
          const currentVal = values.message || '';
          const newVal = currentVal ? `${currentVal} ${promptText}` : promptText;
          form.change('message', newVal);
        };

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
            {/* Mobile Nav Header */}
            <div className={css.mobileNavRow}>
              {onCloseInquiryModal ? (
                <button
                  type="button"
                  className={css.mobileBackBtn}
                  onClick={onCloseInquiryModal}
                  aria-label="Back"
                >
                  <FiArrowLeft />
                </button>
              ) : null}
              <span className={css.mobileNavTitle}>
                <FormattedMessage id="InquiryForm.chatWithSeller" defaultMessage="CHAT WITH SELLER" />
              </span>
            </div>

            {/* Header Section */}
            <div className={css.headerSection}>
              <h1 className={css.mainHeading}>
                Message {authorName || 'seller'} about this item.
              </h1>
              <p className={css.subHeading}>
                Be friendly, clear and keep your communication on the app.
              </p>
            </div>

            {/* 2-Column Content Grid */}
            <div className={css.formGrid}>
              {/* Left Column: Item Card & Seller Card */}
              <div className={css.leftColumn}>
                {/* Product Card */}
                <div className={css.itemCard}>
                  <div className={css.itemImageWrapper}>
                    {firstImage ? (
                      <ResponsiveImage
                        className={css.itemImage}
                        alt={listingTitle || ''}
                        image={firstImage}
                        variants={['square-small', 'square-small2x']}
                      />
                    ) : (
                      <div className={css.itemImagePlaceholder} />
                    )}
                  </div>
                  <div className={css.itemMeta}>
                    <h2 className={css.itemTitle}>{listingTitle}</h2>
                    {sizeConditionText ? (
                      <div className={css.itemSubtitle}>{sizeConditionText}</div>
                    ) : null}
                    {priceVal ? (
                      <div className={css.itemPrice}>{formatMoney(intl, priceVal)}</div>
                    ) : null}
                  </div>
                </div>

                {/* Seller Card */}
                <div className={css.sellerCard}>
                  <div className={css.sellerAvatarWrapper}>
                    <AvatarLarge user={author} className={css.sellerAvatar} disableProfileLink={true} />
                  </div>
                  <div className={css.sellerInfo}>
                    <div className={css.sellerNameRow}>
                      <span className={css.sellerName}>{displayNameDisplay.toUpperCase()}</span>
                      {isEmailVerified ? <VerifySeller className={css.verifiedBadge} /> : null}
                    </div>
                    {renderActivityStatus()}
                    {renderRating()}
                  </div>
                </div>
              </div>

              {/* Right Column: Message Input, Prompt Chips, Submit */}
              <div className={css.rightColumn}>
                <div className={css.sectionTitle}>
                  <FormattedMessage id="InquiryForm.yourMessageTitle" defaultMessage="YOUR MESSAGE" />
                </div>

                <div className={css.safetyWarningBanner}>
                  <IoShieldCheckmarkOutline className={css.safetyWarningIcon} />
                  <span>
                    <FormattedMessage id="InquiryForm.safetyWarningText" />
                  </span>
                </div>

                <FieldTextInput
                  className={css.field}
                  type="textarea"
                  name="message"
                  id={formId ? `${formId}.message` : 'message'}
                  placeholder={messagePlaceholder}
                  validate={messageValidators}
                />

                {/* Quick Prompt Chips */}
                <div className={css.promptChips}>
                  <button
                    type="button"
                    className={css.chipButton}
                    onClick={() => handlePromptClick('Is this still available?')}
                  >
                    Is this still available?
                  </button>
                  <button
                    type="button"
                    className={css.chipButton}
                    onClick={() => handlePromptClick('Can you send fit pics?')}
                  >
                    Can you send fit pics?
                  </button>
                  <button
                    type="button"
                    className={css.chipButton}
                    onClick={() => handlePromptClick('Would you accept an offer?')}
                  >
                    Would you accept an offer?
                  </button>
                </div>

                {/* Submit Wrapper */}
                <div className={css.submitWrapper}>
                  <ErrorMessage error={sendInquiryError} />
                  <PrimaryButton
                    type="submit"
                    className={css.submitButton}
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage id="InquiryForm.submitButtonText" defaultMessage="SEND MESSAGE" />
                  </PrimaryButton>
                </div>

                {/* Mobile Footer Note */}
                <p className={css.footerNote}>
                  Keep communication and payments on KK for buyer protection.
                </p>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default InquiryForm;
